<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { X } from 'lucide-vue-next';
  import { useFormBuilderToast } from '../../composables/useFormBuilderToast';
  import { useMatrixCategoryCreation } from '../../composables/useMatrixCategoryCreation';
  import { ColoredButton, Chip, IconImage } from '@/shared/ui';
  import { Input } from '@/shared/ui/input';
  import { Label } from '@/shared/ui/label';
  import { SearchCombobox, type SearchComboboxOption } from '@/shared/ui/search-combobox';
  import SaveIcon from '@/shared/assets/icons/SaveIcon.svg';
  import type { PageOrientation } from '@record-fill';
  import type { FormElement } from '../../model/form.types';
  import { serializeForm, type FormDefinition } from '../../lib/form-serializer';
  import { applyHeaderRecordFields, readHeaderRecordFields } from '../../lib/form-header-fields';
  import {
    MODEL_TITLE_CONFLICT_MESSAGE,
    extractDraftPrefixTakenMessage,
    isModelTitleConflictError,
    matrixBuilderApi,
    matrixModelApi,
  } from '../../api/form-builder.api';

  type SaveMode = 'draft' | 'template';
  type SaveContext = 'matrix' | 'rules' | 'permissions';

  const props = withDefaults(
    defineProps<{
      elements: FormElement[];
      initialMode?: SaveMode;
      allowedModes?: SaveMode[];
      saveContext?: SaveContext;
      hideModeSelector?: boolean;
      rulesStructure?: object | null;
      permissionsStructure?: object | null;
      categoryOptions?: SearchComboboxOption[];
      orientation?: PageOrientation;
      recordPrefix?: string;
      recordTitle?: string;
    }>(),
    {
      initialMode: 'draft',
      allowedModes: () => ['draft', 'template'],
      saveContext: 'matrix',
      hideModeSelector: false,
      rulesStructure: null,
      permissionsStructure: null,
      categoryOptions: () => [],
      orientation: 'portrait',
      recordPrefix: undefined,
      recordTitle: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'saved', matrixId: number, mode: SaveMode): void;
    (e: 'headerTitleChanged', title: string): void;
  }>();

  const { showSuccessToast, showErrorToast } = useFormBuilderToast();

  const headerFields = readHeaderRecordFields(props.elements);
  const draftPrefix = (props.recordPrefix ?? headerFields.prefix).trim();
  const draftTitle = (props.recordTitle ?? headerFields.title).trim();

  const mode = ref<SaveMode>(props.initialMode);
  const title = ref(props.initialMode === 'draft' ? draftTitle : '');
  const prefix = computed(() => draftPrefix);
  const currentCategoryValue = ref('');
  const categorySearchTerm = ref('');
  const selectedCategories = ref<SearchComboboxOption[]>([]);
  const isSubmitting = ref(false);
  const titleConflictMessage = ref('');

  const canSaveAsDraft = computed(() => props.allowedModes.includes('draft'));
  const canSaveAsTemplate = computed(() => props.allowedModes.includes('template'));
  const categoryOptions = ref<SearchComboboxOption[]>([...props.categoryOptions]);
  const isDraftOnlyContext = computed(() => props.saveContext !== 'matrix');
  const saveTitle = computed(() =>
    isDraftOnlyContext.value ? 'Salvar como rascunho:' : 'Salvar como:'
  );

  const availableCategoryOptions = computed<SearchComboboxOption[]>(() => {
    const term = categorySearchTerm.value.trim().toLowerCase();
    return categoryOptions.value.filter(
      (option) =>
        !selectedCategories.value.some((selected) => selected.value === option.value) &&
        (term === '' || option.label.toLowerCase().includes(term))
    );
  });

  const { isCreatingCategory, requestCategory } = useMatrixCategoryCreation(
    categoryOptions,
    selectCategory
  );

  const categoryEmptyMessage = computed(() =>
    categorySearchTerm.value.trim()
      ? 'Nenhum resultado encontrado. Pressione Enter para cadastrar.'
      : 'Nenhum resultado encontrado.'
  );

  const isFormValid = computed(() => {
    if (!title.value.trim()) return false;
    if (mode.value === 'draft') return prefix.value.trim().length > 0;
    return selectedCategories.value.length > 0;
  });

  watch(
    () => props.categoryOptions,
    (options) => {
      categoryOptions.value = [...options];
    }
  );

  watch(mode, (newMode) => {
    title.value = newMode === 'draft' ? draftTitle : '';
  });

  // Either field can resolve a title conflict, since the rule only holds within shared categories.
  watch(
    [title, selectedCategories],
    () => {
      titleConflictMessage.value = '';
    },
    { deep: true }
  );

  watch(
    () => props.allowedModes,
    (allowedModes) => {
      if (!allowedModes.includes(mode.value)) {
        mode.value = allowedModes[0] ?? 'draft';
      }
    },
    { immediate: true }
  );

  function selectCategory(option: SearchComboboxOption) {
    if (!selectedCategories.value.some((c) => c.value === option.value)) {
      selectedCategories.value.push(option);
    }
    currentCategoryValue.value = '';
    categorySearchTerm.value = '';
  }

  function handleCategorySelect(value: string) {
    if (!value) return;
    const option = categoryOptions.value.find((o) => o.value === value);
    if (option) {
      selectCategory(option);
    } else {
      currentCategoryValue.value = '';
      categorySearchTerm.value = '';
    }
  }

  function removeCategory(value: string) {
    selectedCategories.value = selectedCategories.value.filter((c) => c.value !== value);
  }

  function buildFormDefinition(
    prefixValue: string,
    titleValue: string,
    elements: FormElement[] = props.elements
  ): FormDefinition {
    const serialized = serializeForm(elements, {
      prefix: prefixValue,
      title: titleValue,
      orientation: props.orientation,
    });
    return JSON.parse(serialized) as FormDefinition;
  }

  function resetState() {
    title.value = '';
    currentCategoryValue.value = '';
    categorySearchTerm.value = '';
    selectedCategories.value = [];
  }

  function handleClose() {
    emit('close');
  }

  function getDraftSuccessMessage(): string {
    return isDraftOnlyContext.value ? 'Registro salvo com sucesso.' : 'Matriz salva com sucesso';
  }

  function getDraftErrorMessage(): string {
    return isDraftOnlyContext.value
      ? 'Erro ao salvar Registro, tente novamente.'
      : 'Erro ao salvar registro';
  }

  async function saveAsDraft(): Promise<number> {
    const savedTitle = title.value.trim();
    const elements = applyHeaderRecordFields(props.elements, {
      prefix: prefix.value,
      rev: headerFields.rev,
      title: savedTitle,
    });
    const matrixStructure = buildFormDefinition(prefix.value, savedTitle, elements);
    const created = await matrixBuilderApi.saveMatrix({
      prefix: prefix.value,
      title: savedTitle,
      matrixStructure,
      rulesStructure: props.rulesStructure,
      permissionsStructure: props.permissionsStructure,
      isDraft: true,
    });
    return created.id;
  }

  async function saveAsTemplate(): Promise<number> {
    const structure = buildFormDefinition('MODELO', title.value.trim());
    const created = await matrixModelApi.create({
      title: title.value.trim(),
      structure,
      categoryIds: selectedCategories.value.map((c) => Number(c.value)),
    });
    return created.id;
  }

  async function handleSubmit() {
    if (!isFormValid.value || isSubmitting.value || isCreatingCategory.value) return;

    isSubmitting.value = true;
    try {
      const savedId = mode.value === 'draft' ? await saveAsDraft() : await saveAsTemplate();
      const savedTitle = title.value.trim();
      if (mode.value === 'draft' && savedTitle !== headerFields.title) {
        emit('headerTitleChanged', savedTitle);
      }

      showSuccessToast({
        title: mode.value === 'draft' ? getDraftSuccessMessage() : 'Modelo salvo com sucesso',
        type: 'success',
        hideIcon: true,
      });

      emit('saved', savedId, mode.value);
      resetState();
      emit('close');
    } catch (error) {
      if (mode.value === 'template' && isModelTitleConflictError(error)) {
        titleConflictMessage.value = MODEL_TITLE_CONFLICT_MESSAGE;
        return;
      }

      console.error('Error saving form:', error);
      showErrorToast({
        title:
          mode.value === 'draft'
            ? (extractDraftPrefixTakenMessage(error) ?? getDraftErrorMessage())
            : 'Erro ao salvar modelo',
        type: 'error',
        hideIcon: true,
      });
    } finally {
      isSubmitting.value = false;
    }
  }

  defineExpose({
    mode,
    title,
    prefix,
    selectedCategories,
    categorySearchTerm,
    categoryEmptyMessage,
    availableCategoryOptions,
    isSubmitting,
    isCreatingCategory,
    isFormValid,
    titleConflictMessage,
    setMode: (m: SaveMode) => {
      mode.value = m;
    },
    setTitle: (t: string) => {
      title.value = t;
    },
    setSelectedCategories: (c: SearchComboboxOption[]) => {
      selectedCategories.value = c;
    },
    setCategorySearchTerm: (term: string) => {
      categorySearchTerm.value = term;
    },
    handleCategorySelect,
    removeCategory,
    handleClose,
    handleSubmit,
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col w-[436px] bg-white-01 border border-[var(--color-gray-02)] rounded-2xl shadow-xl"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <div class="flex h-12 items-start justify-end p-3">
      <button
        type="button"
        aria-label="Fechar"
        class="cursor-pointer"
        :disabled="isSubmitting"
        @click="handleClose"
      >
        <X class="size-6 text-[var(--color-gray-07)]" />
      </button>
    </div>

    <div class="flex flex-col items-center gap-3 px-6 pb-6">
      <div class="flex flex-col items-center gap-1.5 w-full">
        <IconImage
          :src="SaveIcon"
          alt=""
          class="size-6"
        />
        <p class="text-sm font-semibold text-[var(--color-gray-07)]">
          {{ saveTitle }}
        </p>
      </div>

      <div class="flex flex-col gap-6 w-full">
        <div
          v-if="!hideModeSelector"
          class="flex gap-8"
        >
          <label
            v-if="canSaveAsDraft"
            class="flex items-center gap-3 cursor-pointer h-5"
          >
            <input
              v-model="mode"
              type="radio"
              value="draft"
              class="size-4 accent-[var(--theme-secondary)] cursor-pointer"
              :disabled="isSubmitting"
            >
            <span class="text-sm font-medium text-[var(--color-gray-07)]">Rascunho</span>
          </label>
          <label
            v-if="canSaveAsTemplate"
            class="flex items-center gap-3 cursor-pointer h-5"
          >
            <input
              v-model="mode"
              type="radio"
              value="template"
              class="size-4 accent-[var(--theme-secondary)] cursor-pointer"
              :disabled="isSubmitting"
            >
            <span class="text-sm font-medium text-[var(--color-gray-07)]">Modelo</span>
          </label>
        </div>

        <div
          v-if="mode === 'draft'"
          class="flex flex-col gap-1.5"
        >
          <Label class="text-sm font-medium text-[var(--color-gray-07)]">Registro</Label>
          <div class="flex items-center gap-1.5">
            <span class="text-sm text-[var(--color-gray-03)]">RQ</span>
            <Input
              :model-value="prefix"
              placeholder="Ex.: 10.3"
              class="w-[100px] h-11"
              :disabled="true"
            />
          </div>
          <p
            v-if="!prefix"
            data-testid="save-form-header-hint"
            class="text-sm text-gray-03"
          >
            Preencha o RQ no cabeçalho do formulário.
          </p>
        </div>

        <div class="flex flex-col gap-1.5">
          <Label class="text-sm font-medium text-[var(--color-gray-07)]">Título</Label>
          <Input
            v-model="title"
            :placeholder="
              mode === 'draft' ? 'Escreva o título do Registro' : 'Escreva o título do Modelo'
            "
            class="h-11"
            :class="{ 'border-red-16': titleConflictMessage }"
            :aria-invalid="titleConflictMessage ? 'true' : undefined"
            :disabled="isSubmitting"
          />
          <p
            v-if="titleConflictMessage"
            data-testid="save-form-title-conflict"
            class="text-red-16 text-sm/5"
          >
            {{ titleConflictMessage }}
          </p>
        </div>

        <div
          v-if="mode === 'template'"
          class="flex flex-col gap-1.5 w-full"
        >
          <Label class="text-sm font-medium text-[var(--color-gray-07)]">Categorias</Label>
          <SearchCombobox
            v-model="currentCategoryValue"
            :options="availableCategoryOptions"
            placeholder="Buscar"
            search-placeholder="Buscar"
            trigger-icon="search"
            class="h-11"
            creatable
            :empty-message="categoryEmptyMessage"
            :disabled="isSubmitting || isCreatingCategory"
            @update:model-value="handleCategorySelect"
            @search="categorySearchTerm = $event"
            @create="requestCategory"
          />
          <div
            v-if="selectedCategories.length"
            class="flex flex-wrap gap-1 pt-0.5"
          >
            <Chip
              v-for="cat in selectedCategories"
              :key="cat.value"
              :label="cat.label"
              @remove="removeCategory(cat.value)"
            />
          </div>
        </div>

        <div class="flex gap-5 w-full">
          <ColoredButton
            text="Cancelar"
            outlined
            border-color="gray-03"
            color="gray-03"
            class="flex-1 justify-center"
            :disabled="isSubmitting"
            @click="handleClose"
          />
          <ColoredButton
            text="Salvar"
            class="flex-1 justify-center"
            :disabled="!isFormValid || isSubmitting || isCreatingCategory"
            @click="handleSubmit"
          />
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>
